    <p-toast></p-toast>
    <app-page-shell>
      <app-page-hero eyebrow="HR Settings" title="Document Types" subtitle="Define required employee document rules, verification needs, expiry warnings, and logistics blockers.">
        <div actions>
          <button pButton type="button" label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="load()"></button>
        </div>
      </app-page-hero>

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      @if (canManageDocumentTypes) {
        <app-hr-action-dialog [(visible)]="formDialogVisible" [title]="form.documentTypeId ? 'Edit Document Type' : 'New Document Type'" eyebrow="Document Setup" subtitle="Define verification, validity, and operational blocking behavior." icon="fa-solid fa-file-shield" [contextTitle]="form.documentName || form.documentCode || 'New document type'" contextSubtitle="Settings apply to HR documents and readiness snapshots.">
          <app-page-form-section icon="fa-solid fa-file-shield" [title]="form.documentTypeId ? 'Edit Document Type' : 'New Document Type'" subtitle="Settings apply to HR employee documents and readiness snapshots.">
          <div class="form-grid">
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="documentCode" pInputText [(ngModel)]="form.documentCode" />
              <label for="documentCode">Document code</label>
            </p-iftaLabel>
          </p-inputGroup>

          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-file-lines"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="documentName" pInputText [(ngModel)]="form.documentName" />
              <label for="documentName">Document name</label>
            </p-iftaLabel>
          </p-inputGroup>

          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-user-gear"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="workerCategory" [options]="workerCategoryOptions" optionLabel="label" optionValue="value" [(ngModel)]="form.appliesToWorkerCategory" [showClear]="true" styleClass="w-full"></p-select>
              <label for="workerCategory">Worker category</label>
            </p-iftaLabel>
          </p-inputGroup>

          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-earth-asia"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="countryCode" pInputText [(ngModel)]="form.countryCode" maxlength="10" />
              <label for="countryCode">Country code</label>
            </p-iftaLabel>
          </p-inputGroup>

          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-lock"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="confidentiality" [options]="confidentialityOptions" optionLabel="label" optionValue="value" [(ngModel)]="form.confidentialityLevel" styleClass="w-full"></p-select>
              <label for="confidentiality">Confidentiality</label>
            </p-iftaLabel>
          </p-inputGroup>

          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-calendar-days"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="expiryWarningDays" pInputText type="number" [(ngModel)]="form.expiryWarningDays" />
              <label for="expiryWarningDays">Expiry warning days</label>
            </p-iftaLabel>
          </p-inputGroup>

          <p-inputGroup class="wide">
            <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <textarea id="description" pTextarea [(ngModel)]="form.description" rows="3"></textarea>
              <label for="description">Description</label>
            </p-iftaLabel>
          </p-inputGroup>
          </div>

          <div class="switch-grid">
            <label><p-toggleswitch [(ngModel)]="form.requiredDocument"></p-toggleswitch><span>Required</span></label>
            <label><p-toggleswitch [(ngModel)]="form.requiresNumber"></p-toggleswitch><span>Number required</span></label>
            <label><p-toggleswitch [(ngModel)]="form.requiresIssueDate"></p-toggleswitch><span>Issue date</span></label>
            <label><p-toggleswitch [(ngModel)]="form.requiresExpiryDate"></p-toggleswitch><span>Expiry date</span></label>
            <label><p-toggleswitch [(ngModel)]="form.requiresFile"></p-toggleswitch><span>File required</span></label>
            <label><p-toggleswitch [(ngModel)]="form.requiresVerification"></p-toggleswitch><span>Verification</span></label>
            <label><p-toggleswitch [(ngModel)]="form.blocksDispatch"></p-toggleswitch><span>Blocks dispatch</span></label>
            <label><p-toggleswitch [(ngModel)]="form.blocksMobilization"></p-toggleswitch><span>Blocks mobilization</span></label>
            <label><p-toggleswitch [(ngModel)]="form.blocksPayroll"></p-toggleswitch><span>Blocks payroll</span></label>
            <label><p-toggleswitch [(ngModel)]="form.blocksOnboardingCompletion"></p-toggleswitch><span>Blocks onboarding</span></label>
            <label><p-toggleswitch [(ngModel)]="form.active"></p-toggleswitch><span>Active</span></label>
          </div>

          <div class="form-actions">
            <button pButton type="button" label="Save Type" icon="pi pi-save" (click)="save()" [disabled]="!form.documentCode || !form.documentName"></button>
            <button pButton type="button" label="Reset" icon="pi pi-undo" severity="secondary" [outlined]="true" (click)="resetForm()"></button>
          </div>
          </app-page-form-section>
        </app-hr-action-dialog>
      }

      <app-page-data-panel title="Configured Types" subtitle="Tenant document rules cloned from the HR Phase 2 logistics baseline." [resultText]="documentTypes.length + ' types'">
        @if (canManageDocumentTypes) {
          <div actions><button pButton type="button" label="New Document Type" icon="pi pi-plus" (click)="resetForm(); formDialogVisible = true"></button></div>
        }
        <p-table [value]="documentTypes" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1160px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Type</th>
              <th>Worker</th>
              <th>Requirements</th>
              <th>Blocks</th>
              <th>Warning</th>
              <th>Confidentiality</th>
              <th>Status</th>
              <th style="width: 5rem">Edit</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-type>
            <tr>
              <td><strong>{{ type.documentName }}</strong><br><small>{{ type.documentCode }}</small></td>
              <td>{{ format(type.appliesToWorkerCategory) }}</td>
              <td>
                <div class="chip-list">
                  @if (type.requiredDocument) { <span>Required</span> }
                  @if (type.requiresNumber) { <span>Number</span> }
                  @if (type.requiresIssueDate) { <span>Issue</span> }
                  @if (type.requiresExpiryDate) { <span>Expiry</span> }
                  @if (type.requiresFile) { <span>File</span> }
                  @if (type.requiresVerification) { <span>Verify</span> }
                </div>
              </td>
              <td>
                <div class="chip-list warn">
                  @if (type.blocksDispatch) { <span>Dispatch</span> }
                  @if (type.blocksMobilization) { <span>Mobilization</span> }
                  @if (type.blocksPayroll) { <span>Payroll</span> }
                  @if (type.blocksOnboardingCompletion) { <span>Onboarding</span> }
                </div>
              </td>
              <td>{{ type.expiryWarningDays || 0 }} days</td>
              <td><p-tag [value]="format(type.confidentialityLevel)" severity="info"></p-tag></td>
              <td><p-tag [value]="type.active ? 'Active' : 'Inactive'" [severity]="type.active ? 'success' : 'secondary'"></p-tag></td>
              <td>
                @if (canManageDocumentTypes) {
                  <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true" (click)="edit(type)"></button>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="8">No HR document types found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
